import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import { format, isAfter, isSameDay, isWithinInterval } from 'date-fns';
import Button from '../Button';
import Badge from '../Badge';
import Divider from '../Divider';
import { getInputBadgeColor } from '../../utils';
import CalendarGrid from './CalendarGrid';
import ComparisonPanel from './ComparisonPanel';
import Presets from './Presets';
import SelectedDateHeader from './SelectedDateHeader';
import { StyledDatePickerBody, StyledDatePickerContainer, StyledDatePickerContent, StyledFooter, StyledMonthContainer, StyledCalendarContentWrapper, StyledSidebar, StyledSidebarContent, StyledMessage } from './styles';
import { useDateRangeWithComparison, useDateRangeWithOverlap, useDragRangeSelection, useMonthsToRender, useSingleDateWithComparison, useZagDatePicker } from './hooks';
import { clampDateRange, findSelectableDate, fromCalendarDate, holdsSameValue, isDateOutsideBounds, isRangeEndInComparisonRange, resolveRangeSelection, toCalendarDate } from './utils';
import type { DatePickerV2FooterProps, DatePickerV2Props, DatePreset, DateRange, DateTileProps, RangeComparisonMode, RangeOverlapMode, SelectionMode, SingleComparisonMode } from './types';
import { CUSTOM_PRESET_VALUE } from './constants';
type DatePickerV2InternalProps = DatePickerV2Props & DatePickerV2FooterProps;

/**
 * How close to an edge of the scrolled month list the user must get before more
 * months are rendered. Roughly one month of content, so the next batch is ready
 * before the user reaches the end of the list.
 */
const MONTH_EXTEND_THRESHOLD_PX = 320;
type ZagApi = ReturnType<typeof useZagDatePicker>['zagApi'];
type SingleCompHook = ReturnType<typeof useSingleDateWithComparison>;
type RangeCompHook = ReturnType<typeof useDateRangeWithComparison>;
type RangeOverlapHook = ReturnType<typeof useDateRangeWithOverlap>;
type DateRangeHook = RangeCompHook['mainRange'];
type BaseTileProps = Pick<DateTileProps, 'date' | 'isToday' | 'isDisabled'>;
type OverlapExtras = Pick<DateTileProps, 'isOverlap' | 'isOverlapStart' | 'isOverlapEnd' | 'isRangeEndInComparisonRange'>;
const normalizeDragRange = (start: Date, current: Date): {
  from: Date;
  to: Date;
} => isAfter(current, start) ? {
  from: start,
  to: current
} : {
  from: current,
  to: start
};
const getDragRangeFlags = (drag: {
  start: Date;
  current: Date;
}, date: Date): {
  inRange: boolean;
  isStart: boolean;
  isEnd: boolean;
} => {
  const {
    from,
    to
  } = normalizeDragRange(drag.start, drag.current);
  return {
    inRange: isWithinInterval(date, {
      start: from,
      end: to
    }),
    isStart: isSameDay(date, from),
    isEnd: isSameDay(date, to)
  };
};
const getRangeFlags = (hook: DateRangeHook, date: Date): {
  inRange: boolean;
  isStart: boolean;
  isEnd: boolean;
} => {
  if (hook.range != null) {
    return {
      inRange: hook.isDateInRange(date),
      isStart: hook.isRangeStart(date),
      isEnd: hook.isRangeEnd(date)
    };
  }
  // After the first click but before the second: render the pending start as a selected endpoint.
  if (hook.tempStart != null && isSameDay(date, hook.tempStart)) {
    return {
      inRange: false,
      isStart: true,
      isEnd: false
    };
  }
  return {
    inRange: false,
    isStart: false,
    isEnd: false
  };
};
const rangeOrTempStart = (hook: DateRangeHook): DateRange | null => {
  if (hook.range != null) return hook.range;
  if (hook.tempStart != null) return {
    from: hook.tempStart
  };
  return null;
};
const buildZagTileProps = (zagApi: ZagApi, date: Date, mode: SelectionMode): Partial<DateTileProps> => {
  const cellState = zagApi.getDayTableCellState({
    value: toCalendarDate(date)
  });
  const base = {
    date,
    isToday: cellState.today,
    isDisabled: cellState.disabled
  };
  if (mode === 'single') {
    return {
      ...base,
      isSelected: cellState.selected
    };
  }
  return {
    ...base,
    isInRange: cellState.inRange,
    isRangeStart: cellState.firstInRange,
    isRangeEnd: cellState.lastInRange,
    isSelected: cellState.firstInRange || cellState.lastInRange
  };
};
const buildSingleComparisonTileProps = (baseProps: BaseTileProps, hook: SingleCompHook, date: Date): Partial<DateTileProps> => ({
  ...baseProps,
  isSelected: hook.selectedDate != null ? isSameDay(date, hook.selectedDate) : false,
  isComparisonDate: hook.comparisonDate != null ? isSameDay(date, hook.comparisonDate) : false
});
const buildRangePairTileProps = (baseProps: BaseTileProps, mainHook: DateRangeHook, compHook: DateRangeHook, date: Date, extras: OverlapExtras = {}): Partial<DateTileProps> => {
  const main = getRangeFlags(mainHook, date);
  const comp = getRangeFlags(compHook, date);
  return {
    ...baseProps,
    isInRange: main.inRange,
    isRangeStart: main.isStart,
    isRangeEnd: main.isEnd,
    isInComparisonRange: comp.inRange,
    isComparisonRangeStart: comp.isStart,
    isComparisonRangeEnd: comp.isEnd,
    isSelected: main.isStart || main.isEnd || comp.isStart || comp.isEnd,
    ...extras
  };
};
interface RangePreviewFlags {
  inRange: boolean;
  isStart: boolean;
  isEnd: boolean;
}
const applyRangePreviewFlags = (tileProps: Partial<DateTileProps>, flags: RangePreviewFlags, target: 'main' | 'comparison'): Partial<DateTileProps> => {
  if (target === 'comparison') {
    return {
      ...tileProps,
      isInComparisonRange: flags.inRange,
      isComparisonRangeStart: flags.isStart,
      isComparisonRangeEnd: flags.isEnd,
      isSelected: tileProps.isRangeStart === true || tileProps.isRangeEnd === true || flags.isStart || flags.isEnd
    };
  }
  return {
    ...tileProps,
    isInRange: flags.inRange,
    isRangeStart: flags.isStart,
    isRangeEnd: flags.isEnd,
    isSelected: flags.isStart || flags.isEnd || tileProps.isComparisonRangeStart === true || tileProps.isComparisonRangeEnd === true
  };
};
const computeSelectedDate = (mode: SelectionMode, zagApi: ZagApi, singleComp: SingleCompHook): Date | null => {
  if (mode === 'single') {
    return zagApi.value.length > 0 ? fromCalendarDate(zagApi.value[0]) : null;
  }
  if (mode === 'single-comparison') return singleComp.selectedDate;
  return null;
};
const computeSelectedRange = (mode: SelectionMode, zagApi: ZagApi, rangeComp: RangeCompHook, rangeOverlap: RangeOverlapHook): DateRange | null => {
  if (mode === 'range') {
    if (zagApi.value.length === 0) return null;
    const from = fromCalendarDate(zagApi.value[0]);
    const to = zagApi.value.length > 1 ? fromCalendarDate(zagApi.value[1]) : undefined;
    return {
      from,
      to
    };
  }
  if (mode === 'range-comparison') return rangeOrTempStart(rangeComp.mainRange);
  if (mode === 'range-overlap') return rangeOrTempStart(rangeOverlap.mainRange);
  return null;
};
const computeComparisonRange = (mode: SelectionMode, rangeComp: RangeCompHook, rangeOverlap: RangeOverlapHook): DateRange | null => {
  if (mode === 'range-comparison') return rangeOrTempStart(rangeComp.comparisonRange);
  if (mode === 'range-overlap') return rangeOrTempStart(rangeOverlap.comparisonRange);
  return null;
};
const computeHasSelection = (mode: SelectionMode, zagApi: ZagApi, singleComp: SingleCompHook, rangeComp: RangeCompHook, rangeOverlap: RangeOverlapHook): boolean => {
  switch (mode) {
    case 'single':
      return zagApi.value.length > 0;
    case 'range':
      return zagApi.value.length === 2;
    case 'single-comparison':
      return singleComp.selectedDate != null;
    case 'range-comparison':
      return rangeComp.mainRange.range != null;
    case 'range-overlap':
      return rangeOverlap.mainRange.range != null;
  }
};
const applyPresetToRangeHook = (mainRangeHook: DateRangeHook, dateRange: DateRange): void => {
  if (dateRange.to != null) {
    mainRangeHook.setRange({
      from: dateRange.from,
      to: dateRange.to
    });
  } else {
    mainRangeHook.selectDateRange(dateRange.from);
  }
};
const resolveActivePresetValue = (props: DatePickerV2Props): string | undefined => {
  if (props.mode !== 'range' && props.mode !== 'range-comparison' && props.mode !== 'range-overlap') return undefined;
  const {
    range,
    presetValue
  } = resolveRangeSelection(props.selectedRange, props.selectedPreset, props.presets?.getDateRangeFromPreset, props.maxDate, props.minDate);

  // The resolver reports "custom" whenever no preset matched, including when
  // nothing is selected at all. Only surface it once a range exists, so an empty
  // picker doesn't open with the Custom radio pre-checked.
  if (presetValue !== CUSTOM_PRESET_VALUE) return presetValue;
  return range != null ? CUSTOM_PRESET_VALUE : undefined;
};
export const getModeAnchorDate = (props: DatePickerV2Props): Date | null => {
  switch (props.mode) {
    case 'single':
    case 'single-comparison':
      return props.selectedDate;
    case 'range':
    case 'range-comparison':
    case 'range-overlap':
      return resolveRangeSelection(props.selectedRange, props.selectedPreset, props.presets?.getDateRangeFromPreset, props.maxDate, props.minDate).range?.from ?? null;
  }
};
const DatePickerV2 = ({
  ref,
  ...props
}: DatePickerV2InternalProps & {
  ref?: React.Ref<HTMLDivElement>;
}): React.ReactElement => {
  const propsRef = useRef(props);
  propsRef.current = props;
  const pendingRangePresetRef = useRef<string | null>(null);
  const [activePresetValue, setActivePresetValue] = useState<string | undefined>(() => resolveActivePresetValue(props));

  // Follow a controlled `selectedPreset` while the picker stays mounted. Comparing
  // against the previous prop (not against state) leaves in-session selections in
  // charge of the radio until the consumer changes the prop again.
  const selectedPresetProp = props.mode === 'range' || props.mode === 'range-comparison' || props.mode === 'range-overlap' ? props.selectedPreset : undefined;
  const [lastSelectedPresetProp, setLastSelectedPresetProp] = useState(selectedPresetProp);
  if (selectedPresetProp !== lastSelectedPresetProp) {
    setLastSelectedPresetProp(selectedPresetProp);
    setActivePresetValue(resolveActivePresetValue(props));
  }
  const notifySelectRange = useCallback((range: DateRange | null, presetValue: string): void => {
    setActivePresetValue(presetValue);
    const current = propsRef.current;
    if (current.mode === 'range' || current.mode === 'range-comparison' || current.mode === 'range-overlap') {
      current.onSelectRange(range, presetValue);
    }
  }, []);
  const zagPickerProps: DatePickerV2Props = props.mode === 'range' ? {
    ...props,
    onSelectRange: (range_0, presetValue_0) => {
      notifySelectRange(range_0, presetValue_0);
    }
  } : props;
  const {
    mode,
    minDate,
    maxDate,
    disabled = false,
    showHeader = true,
    presets: presetsConfig,
    onApply,
    onCancel,
    isApplyDisabled,
    viewMonth,
    className,
    firstDayOfWeek = 1,
    shouldDisableDate,
    isOutsideRange,
    disableTextEdit = false,
    helperText,
    color,
    applyText = 'Apply',
    cancelText = 'Cancel',
    renderCalendarInfo,
    onApplyDateRangeClick,
    defaultComparisonEnabled = false,
    lockComparisonEnabled = false,
    comparisonPresets,
    selectedComparisonPreset: selectedComparisonPresetProp,
    defaultComparisonPreset,
    onComparisonPresetChange
  } = props;
  const showPresets = presetsConfig?.showPresets ?? presetsConfig != null;
  const getDateRangeFromPreset = presetsConfig?.getDateRangeFromPreset;
  const presets = presetsConfig?.presets ?? [];
  const comparisonPresetOptions = comparisonPresets?.presets ?? [];
  const getComparisonDateFromPreset = comparisonPresets?.getComparisonDateFromPreset;
  const getComparisonRangeFromPreset = comparisonPresets?.getComparisonRangeFromPreset;
  const effectiveShouldDisableDate = useMemo(() => {
    if (shouldDisableDate == null && isOutsideRange == null) return undefined;
    return (date: Date) => (shouldDisableDate?.(date) ?? false) || (isOutsideRange?.(date) ?? false);
  }, [shouldDisableDate, isOutsideRange]);
  const {
    zagApi,
    service,
    useZag
  } = useZagDatePicker(zagPickerProps, pendingRangePresetRef, getDateRangeFromPreset);

  // `zagApi` is rebuilt every render while callbacks below are keyed on the stable
  // `service`, so its snapshot of the value has to be read through a ref.
  const zagValueRef = useRef(zagApi.value);
  zagValueRef.current = zagApi.value;
  const initialSingleCompDate = props.mode === 'single-comparison' ? props.selectedDate : null;
  const initialSingleComparisonDate = props.mode === 'single-comparison' ? props.comparisonDate : null;
  const initialRange = props.mode === 'range-comparison' || props.mode === 'range-overlap' ? resolveRangeSelection(props.selectedRange, props.selectedPreset, getDateRangeFromPreset, maxDate, minDate).range : null;
  const initialCompRange = props.mode === 'range-comparison' || props.mode === 'range-overlap' ? props.comparisonRange : null;
  const singleComparisonSelection = useSingleDateWithComparison(initialSingleCompDate, initialSingleComparisonDate);
  const rangeComparisonSelection = useDateRangeWithComparison(initialRange, initialCompRange);
  const rangeOverlapSelection = useDateRangeWithOverlap(initialRange, initialCompRange);
  const isRangeMode = mode === 'range' || mode === 'range-comparison' || mode === 'range-overlap';

  // Drag-to-select range. We hand the hook a ref-backed commit so it doesn't
  // capture a stale `handleDragRangeCommit` (which is defined further down
  // and depends on `props`).
  const handleDragRangeCommitRef = useRef<(range: DateRange) => void>(() => undefined);
  const dragSelection = useDragRangeSelection({
    enabled: isRangeMode && !disabled,
    onCommit: useCallback((range_1: DateRange) => {
      handleDragRangeCommitRef.current(range_1);
    }, [])
  });
  const [comparisonEnabled, setComparisonEnabled] = useState<boolean>(defaultComparisonEnabled);
  const [comparisonPresetInternal, setComparisonPresetInternal] = useState<string>(defaultComparisonPreset ?? comparisonPresetOptions[0]?.value ?? CUSTOM_PRESET_VALUE);
  const comparisonPreset = selectedComparisonPresetProp ?? comparisonPresetInternal;
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const comparisonPresetRef = useRef(comparisonPreset);
  comparisonPresetRef.current = comparisonPreset;
  const handleComparisonPresetChange = useCallback((presetValue_1: string): void => {
    setComparisonPresetInternal(presetValue_1);
    onComparisonPresetChange?.(presetValue_1);
  }, [onComparisonPresetChange]);

  // A preset-derived comparison range ("previous period", "previous year", …) is a
  // function of the primary range, so every primary-range change has to recompute it.
  // A `custom` comparison was picked by hand and is left alone.
  const syncComparisonToMainRange = useCallback((newRange: DateRange | null): void => {
    if (!comparisonEnabled) return;
    if (mode !== 'range-comparison' && mode !== 'range-overlap') return;
    if (comparisonPresetRef.current === CUSTOM_PRESET_VALUE) return;
    if (newRange?.to == null) return;
    const newCompRange = getComparisonRangeFromPreset?.(newRange, comparisonPresetRef.current);
    if (newCompRange == null) return;
    if (mode === 'range-comparison') {
      rangeComparisonSelection.comparisonRange.setRange(newCompRange);
      (propsRef.current as RangeComparisonMode).onSelectComparisonRange(newCompRange);
    } else {
      rangeOverlapSelection.comparisonRange.setRange(newCompRange);
      (propsRef.current as RangeOverlapMode).onSelectComparisonRange(newCompRange);
    }
  }, [comparisonEnabled, mode, getComparisonRangeFromPreset, rangeComparisonSelection, rangeOverlapSelection]);
  const anchor = viewMonth ?? getModeAnchorDate(props) ?? new Date();

  // A comparison range can fall far from the main range (e.g. previous-year sits
  // ~12 months earlier). Feed its endpoints to the month window so those tiles
  // stay rendered and can be highlighted, not just shown in the inputs.
  let comparisonRangeForWindow: DateRange | null = null;
  if (mode === 'range-comparison') comparisonRangeForWindow = rangeComparisonSelection.comparisonRange.range;else if (mode === 'range-overlap') comparisonRangeForWindow = rangeOverlapSelection.comparisonRange.range;
  const mustIncludeMonths = useMemo(() => [comparisonRangeForWindow?.from, comparisonRangeForWindow?.to], [comparisonRangeForWindow?.from, comparisonRangeForWindow?.to]);
  const {
    months: monthsToRender,
    anchorIndex,
    extendBackward,
    extendForward,
    canExtendBackward,
    canExtendForward
  } = useMonthsToRender(anchor, minDate, maxDate, mustIncludeMonths);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const anchorMonthRef = useRef<HTMLDivElement | null>(null);

  // Scroll the anchor month into view whenever the anchor changes (e.g. dropdown
  // re-opens). Direct scrollTop assignment is more reliable than scrollIntoView
  // when the wrapper is the scroll container.
  // Subtract ~2 tile rows (84px) so a couple of weeks from the previous month
  // remain visible above the anchor month header, keeping it clear of the
  // SelectedDateHeader above the scroll container.
  const anchorTime = anchor.getTime();
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const anchorEl = anchorMonthRef.current;
    if (wrapper == null || anchorEl == null) return;
    const TWO_ROWS_PX = 168; // 4 × (40px tile height + 2px row gap)
    wrapper.scrollTop = Math.max(0, anchorEl.offsetTop - TWO_ROWS_PX);
  }, [anchorTime]);

  // Distance from the bottom of the scrolled content at the moment a backward
  // extension was requested. Prepending months shifts everything down, so we
  // re-anchor to this distance to keep the user's viewport on the same month.
  const distanceFromEndBeforeExtendRef = useRef<number | null>(null);
  // Wheel and trackpad events keep arriving until the new months commit and
  // scrollTop is restored. Each of those still sees the old edge, so without
  // this lock one gesture queues a 12-month extension per event.
  const monthExtendInFlightRef = useRef(false);
  useLayoutEffect(() => {
    const wrapper_0 = wrapperRef.current;
    const distanceFromEnd = distanceFromEndBeforeExtendRef.current;
    if (wrapper_0 != null && distanceFromEnd != null) {
      distanceFromEndBeforeExtendRef.current = null;
      // Assigning scrollTop fires another scroll event. Keep the lock until
      // that event has been ignored, then allow the next genuine edge reach.
      wrapper_0.scrollTop = wrapper_0.scrollHeight - distanceFromEnd;
    }
    monthExtendInFlightRef.current = false;
  }, [monthsToRender]);
  const handleCalendarScroll = useCallback((): void => {
    if (monthExtendInFlightRef.current) return;
    const wrapper_1 = wrapperRef.current;
    if (wrapper_1 == null) return;
    const {
      scrollTop,
      scrollHeight,
      clientHeight
    } = wrapper_1;
    if (canExtendBackward && scrollTop <= MONTH_EXTEND_THRESHOLD_PX) {
      monthExtendInFlightRef.current = true;
      distanceFromEndBeforeExtendRef.current = scrollHeight - scrollTop;
      extendBackward();
      return;
    }
    if (canExtendForward && scrollHeight - scrollTop - clientHeight <= MONTH_EXTEND_THRESHOLD_PX) {
      monthExtendInFlightRef.current = true;
      extendForward();
    }
  }, [canExtendBackward, canExtendForward, extendBackward, extendForward]);

  // Derive the relevant main range based on mode.
  let mainRangeForAutoCompute: DateRange | null = null;
  if (mode === 'range-comparison') {
    mainRangeForAutoCompute = rangeComparisonSelection.mainRange.range;
  } else if (mode === 'range-overlap') {
    mainRangeForAutoCompute = rangeOverlapSelection.mainRange.range;
  }

  // Keep refs so the effect below can read the latest values without them
  // triggering the effect when they change.
  const mainRangeForAutoComputeRef = useRef(mainRangeForAutoCompute);
  mainRangeForAutoComputeRef.current = mainRangeForAutoCompute;
  let currentComparisonRange = null;
  if (mode === 'range-comparison') currentComparisonRange = rangeComparisonSelection.comparisonRange.range;
  if (mode === 'range-overlap') currentComparisonRange = rangeOverlapSelection.comparisonRange.range;
  const currentComparisonRangeRef = useRef(currentComparisonRange);
  currentComparisonRangeRef.current = currentComparisonRange;

  // On first mount this is true. Flipped to false after the initial effect run.
  // Since DatePickerV2 unmounts every time the dropdown closes, this correctly
  // resets to true on each new open so we can distinguish mount-time from
  // user-triggered runs.
  const isInitialMountRef = useRef(true);

  // Auto-compute the comparison range/date when the user explicitly changes the
  // preset option or toggles comparison on — but NOT when the primary selection
  // changes, and NOT on mount if a committed comparison range was already passed
  // in (we do not want to overwrite it).
  useEffect(() => {
    if (!comparisonEnabled) return;
    if (mode === 'single-comparison') {
      const date_0 = singleComparisonSelection.selectedDate;
      if (date_0 == null) return;
      const newDate = getComparisonDateFromPreset?.(date_0, comparisonPreset);
      if (newDate == null) return;
      singleComparisonSelection.selectComparisonDate(newDate);
      (propsRef.current as SingleComparisonMode).onSelectComparisonDate(newDate);
      return;
    }

    // On the very first effect run after mount: skip if the picker was opened
    // with an existing comparison range — the user already has one committed.
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      if (currentComparisonRangeRef.current != null) return;
    }
    if (mainRangeForAutoComputeRef.current?.to == null) return;
    const newRange_0 = getComparisonRangeFromPreset?.(mainRangeForAutoComputeRef.current, comparisonPreset);
    if (newRange_0 == null) return;
    if (mode === 'range-comparison') {
      rangeComparisonSelection.comparisonRange.setRange(newRange_0);
      (propsRef.current as RangeComparisonMode).onSelectComparisonRange(newRange_0);
    } else if (mode === 'range-overlap') {
      rangeOverlapSelection.comparisonRange.setRange(newRange_0);
      (propsRef.current as RangeOverlapMode).onSelectComparisonRange(newRange_0);
    }
    // Stable setters and refs are intentionally omitted from deps.
    // propsRef.current keeps callbacks fresh without extra deps.
    // mainRangeForAutoComputeRef/currentComparisonRangeRef are read via ref to
    // avoid triggering this effect when those ranges change.
  }, [comparisonEnabled, comparisonPreset, getComparisonDateFromPreset, getComparisonRangeFromPreset, mode, singleComparisonSelection.selectedDate]);
  const handlePresetSelect = useCallback((preset: DatePreset): void => {
    if (preset.disabled === true) return;
    const dateRange = getDateRangeFromPreset?.(preset.value, maxDate);
    if (dateRange == null) return;

    // Preset resolvers may return dates outside [minDate, maxDate] (e.g. allTime
    // from 2000). Clamp before notifying so Apply cannot commit out-of-bounds
    // dates when Zag stays silent because the clamped value already matches.
    const boundedRange = clampDateRange(dateRange, minDate, maxDate);
    if (mode === 'single' || mode === 'single-comparison') {
      // Single-date modes collapse a preset period onto its last day: presets end on
      // the most recent day the consumer has data for, which is the day the user means
      // when picking "Last 30 days" or "All time" in a single-date picker. Taking the
      // start instead would jump back to the beginning of the window.
      // Preset arithmetic knows nothing about days the consumer disables, so land on the
      // closest selectable one — committing a disabled day would query a gap in the data.
      const presetDate = findSelectableDate(boundedRange.to ?? boundedRange.from, effectiveShouldDisableDate, minDate, maxDate);
      if (presetDate == null) return;
      setActivePresetValue(preset.value);
      if (mode === 'single') {
        zagApi.setValue([toCalendarDate(presetDate)]);
        return;
      }
      singleComparisonSelection.selectDate(presetDate);
      (propsRef.current as SingleComparisonMode).onSelectDate(presetDate);
      return;
    }
    setActivePresetValue(preset.value);
    if (mode === 'range') {
      const vals = [toCalendarDate(boundedRange.from)];
      if (boundedRange.to != null) vals.push(toCalendarDate(boundedRange.to));

      // Normally Zag reports the new range through `onValueChange`, which reads the
      // preset back off `pendingRangePresetRef`. It stays silent when the dates it
      // already holds match this preset, and its handler ignores a lone start date,
      // so notify the consumer directly in those cases — otherwise Apply would
      // report `custom` while the radio shows this preset.
      const zagWillReport = vals.length === 2 && !holdsSameValue(zagValueRef.current, vals, minDate, maxDate);
      if (zagWillReport) pendingRangePresetRef.current = preset.value;
      zagApi.setValue(vals);
      if (!zagWillReport) notifySelectRange(boundedRange, preset.value);
      return;
    }
    const mainRangeHook = mode === 'range-comparison' ? rangeComparisonSelection.mainRange : rangeOverlapSelection.mainRange;
    applyPresetToRangeHook(mainRangeHook, boundedRange);
    notifySelectRange(boundedRange, preset.value);

    // Primary-range changes are intentionally excluded from the auto-compare
    // effect deps, so presets must refresh a non-custom comparison here.
    syncComparisonToMainRange(boundedRange);
  },
  // zagApi is a new object each render — use service (stable) as the dep

  [mode, service, singleComparisonSelection, rangeComparisonSelection, rangeOverlapSelection, effectiveShouldDisableDate, getDateRangeFromPreset, syncComparisonToMainRange, minDate, maxDate, notifySelectRange]);
  const getZagDateProps = useCallback((date_1: Date): React.HTMLAttributes<HTMLElement> | undefined => {
    if (!useZag) return undefined;
    return zagApi.getDayTableCellTriggerProps({
      value: toCalendarDate(date_1)
    });
  },
  // zagApi is a new object each render — use service (stable) as the dep

  [useZag, service]);

  // The tempStart of whichever range is currently being filled by a two-click
  // selection. Used to paint a hover preview between first click and second click,
  // matching the live preview that drag selection provides during mousedown.
  const pendingStart = useMemo((): {
    start: Date;
    target: 'main' | 'comparison';
  } | null => {
    if (!isRangeMode) return null;
    if (mode === 'range-comparison') {
      if (comparisonEnabled && rangeComparisonSelection.comparisonRange.tempStart != null) {
        return {
          start: rangeComparisonSelection.comparisonRange.tempStart,
          target: 'comparison'
        };
      }
      if (rangeComparisonSelection.mainRange.tempStart != null) {
        return {
          start: rangeComparisonSelection.mainRange.tempStart,
          target: 'main'
        };
      }
    }
    if (mode === 'range-overlap') {
      if (comparisonEnabled && rangeOverlapSelection.comparisonRange.tempStart != null) {
        return {
          start: rangeOverlapSelection.comparisonRange.tempStart,
          target: 'comparison'
        };
      }
      if (rangeOverlapSelection.mainRange.tempStart != null) {
        return {
          start: rangeOverlapSelection.mainRange.tempStart,
          target: 'main'
        };
      }
    }
    return null;
  }, [isRangeMode, mode, comparisonEnabled, rangeComparisonSelection.mainRange.tempStart, rangeComparisonSelection.comparisonRange.tempStart, rangeOverlapSelection.mainRange.tempStart, rangeOverlapSelection.comparisonRange.tempStart]);

  // Refs so `handleDateHover` can read the latest pending/hovered values without
  // re-creating the callback and without setting state on every idle hover.
  const pendingStartRef = useRef(pendingStart);
  pendingStartRef.current = pendingStart;
  const hoveredDateRef = useRef(hoveredDate);
  hoveredDateRef.current = hoveredDate;

  // Which range a fresh drag should populate. Mirrors the routing in
  // `handleDragRangeCommit` so the live preview paints the correct colour and
  // doesn't blow away the existing main range while the user is sweeping out
  // the comparison range.
  const dragTarget = useMemo((): 'main' | 'comparison' => {
    if (!comparisonEnabled || mode !== 'range-comparison' && mode !== 'range-overlap') return 'main';
    const mainRange = mode === 'range-comparison' ? rangeComparisonSelection.mainRange.range : rangeOverlapSelection.mainRange.range;
    const compRange = mode === 'range-comparison' ? rangeComparisonSelection.comparisonRange.range : rangeOverlapSelection.comparisonRange.range;
    if (mainRange != null && compRange == null) return 'comparison';
    return 'main';
  }, [comparisonEnabled, mode, rangeComparisonSelection.mainRange.range, rangeComparisonSelection.comparisonRange.range, rangeOverlapSelection.mainRange.range, rangeOverlapSelection.comparisonRange.range]);
  const getDateTileProps = useCallback((date_2: Date): Partial<DateTileProps> => {
    let tileProps: Partial<DateTileProps>;
    if (useZag) {
      tileProps = buildZagTileProps(zagApi, date_2, mode);
    } else {
      const baseProps = {
        date: date_2,
        isToday: isSameDay(date_2, new Date()),
        isDisabled: disabled || isDateOutsideBounds(date_2, minDate, maxDate) || (effectiveShouldDisableDate?.(date_2) ?? false)
      };
      if (mode === 'single-comparison') {
        tileProps = buildSingleComparisonTileProps(baseProps, singleComparisonSelection, date_2);
      } else if (mode === 'range-comparison') {
        tileProps = buildRangePairTileProps(baseProps, rangeComparisonSelection.mainRange, rangeComparisonSelection.comparisonRange, date_2, {
          isOverlap: rangeComparisonSelection.isDateInOverlap(date_2),
          isOverlapStart: rangeComparisonSelection.isOverlapStart(date_2),
          isOverlapEnd: rangeComparisonSelection.isOverlapEnd(date_2),
          isRangeEndInComparisonRange: isRangeEndInComparisonRange(date_2, rangeComparisonSelection.mainRange.range, rangeComparisonSelection.comparisonRange.range)
        });
      } else if (mode === 'range-overlap') {
        tileProps = buildRangePairTileProps(baseProps, rangeOverlapSelection.mainRange, rangeOverlapSelection.comparisonRange, date_2, {
          isOverlap: rangeOverlapSelection.isDateInOverlap(date_2),
          isOverlapStart: rangeOverlapSelection.isOverlapStart(date_2),
          isOverlapEnd: rangeOverlapSelection.isOverlapEnd(date_2),
          isRangeEndInComparisonRange: isRangeEndInComparisonRange(date_2, rangeOverlapSelection.mainRange.range, rangeOverlapSelection.comparisonRange.range)
        });
      } else {
        tileProps = baseProps;
      }
    }

    // Live drag preview. When the drag will commit to the main range we
    // override the main flags; when it will commit to the comparison range
    // we override the comparison flags and leave the existing main range
    // visible.
    if (dragSelection.dragRange != null && isRangeMode) {
      return applyRangePreviewFlags(tileProps, getDragRangeFlags(dragSelection.dragRange, date_2), dragTarget);
    }

    // Hover preview for in-progress two-click selection: after the first click
    // (tempStart set) paint the tentative range as the mouse moves, matching
    // the visual feedback drag selection gives during mousedown.
    if (pendingStart != null && hoveredDate != null) {
      return applyRangePreviewFlags(tileProps, getDragRangeFlags({
        start: pendingStart.start,
        current: hoveredDate
      }, date_2), pendingStart.target);
    }
    return tileProps;
  }, [mode, useZag, disabled, minDate, maxDate, zagApi, service, effectiveShouldDisableDate, singleComparisonSelection, rangeComparisonSelection, rangeOverlapSelection, isRangeMode, dragSelection.dragRange, dragTarget, pendingStart, hoveredDate]);
  const handleRangeComparisonClick = useCallback((date_3: Date): void => {
    if (comparisonEnabled && dragTarget === 'comparison') {
      const newRange_1 = rangeComparisonSelection.comparisonRange.selectDateRange(date_3);
      (propsRef.current as RangeComparisonMode).onSelectComparisonRange(newRange_1);
      if (newRange_1 != null && comparisonPresetRef.current !== CUSTOM_PRESET_VALUE) handleComparisonPresetChange(CUSTOM_PRESET_VALUE);
    } else {
      const newRange_2 = rangeComparisonSelection.mainRange.selectDateRange(date_3);
      notifySelectRange(newRange_2, CUSTOM_PRESET_VALUE);
      if (newRange_2 != null) {
        syncComparisonToMainRange(newRange_2);
      } else if (rangeComparisonSelection.comparisonRange.range != null) {
        rangeComparisonSelection.comparisonRange.clearRange();
        (propsRef.current as RangeComparisonMode).onSelectComparisonRange(null);
      }
    }
  }, [comparisonEnabled, dragTarget, rangeComparisonSelection, comparisonPresetRef, handleComparisonPresetChange, syncComparisonToMainRange, propsRef, notifySelectRange]);
  const handleRangeOverlapClick = useCallback((date_4: Date): void => {
    if (comparisonEnabled && dragTarget === 'comparison') {
      const newRange_3 = rangeOverlapSelection.comparisonRange.selectDateRange(date_4);
      (propsRef.current as RangeOverlapMode).onSelectComparisonRange(newRange_3);
      if (newRange_3 != null && comparisonPresetRef.current !== CUSTOM_PRESET_VALUE) handleComparisonPresetChange(CUSTOM_PRESET_VALUE);
    } else {
      const newRange_4 = rangeOverlapSelection.mainRange.selectDateRange(date_4);
      notifySelectRange(newRange_4, CUSTOM_PRESET_VALUE);
      if (newRange_4 != null) {
        syncComparisonToMainRange(newRange_4);
      } else if (rangeOverlapSelection.comparisonRange.range != null) {
        rangeOverlapSelection.comparisonRange.clearRange();
        (propsRef.current as RangeOverlapMode).onSelectComparisonRange(null);
      }
    }
  }, [comparisonEnabled, dragTarget, rangeOverlapSelection, comparisonPresetRef, handleComparisonPresetChange, syncComparisonToMainRange, propsRef, notifySelectRange]);
  const handleDateClick = useCallback((date_5: Date): void => {
    switch (propsRef.current.mode) {
      case 'single':
      case 'range':
        break;
      case 'single-comparison':
        singleComparisonSelection.selectDate(date_5);
        (propsRef.current as SingleComparisonMode).onSelectDate(date_5);
        break;
      case 'range-comparison':
        handleRangeComparisonClick(date_5);
        break;
      case 'range-overlap':
        handleRangeOverlapClick(date_5);
        break;
    }
  }, [singleComparisonSelection, handleRangeComparisonClick, handleRangeOverlapClick, propsRef]);
  const handleDateInputChange = useCallback((date_6: Date): void => {
    switch (propsRef.current.mode) {
      case 'single':
        zagApi.setValue([toCalendarDate(date_6)]);
        break;
      case 'single-comparison':
        singleComparisonSelection.selectDate(date_6);
        (propsRef.current as SingleComparisonMode).onSelectDate(date_6);
        break;
    }
  },
  // zagApi is a new object each render — use service (stable) as the dep

  [singleComparisonSelection, service]);
  const selectedDate = computeSelectedDate(mode, zagApi, singleComparisonSelection);
  const selectedRange = computeSelectedRange(mode, zagApi, rangeComparisonSelection, rangeOverlapSelection);
  const comparisonDate = mode === 'single-comparison' ? singleComparisonSelection.comparisonDate : null;
  const comparisonRange = computeComparisonRange(mode, rangeComparisonSelection, rangeOverlapSelection);
  const hasSelection = computeHasSelection(mode, zagApi, singleComparisonSelection, rangeComparisonSelection, rangeOverlapSelection);
  const isComparisonMode = mode === 'single-comparison' || mode === 'range-comparison' || mode === 'range-overlap';
  const showSelectedHeader = showHeader;
  const showSidebar = showPresets || isComparisonMode;
  const TILE_HEIGHT = 40;
  const ROW_GAP = 2;
  const MONTH_HEADER_HEIGHT = 56;
  const rowsForHeight = (weeks: number): number => weeks * TILE_HEIGHT + (weeks - 1) * ROW_GAP;
  const visibleWeeks = isComparisonMode && comparisonEnabled ? 6 + 1 : 6 + 3;
  const calendarMaxHeight = MONTH_HEADER_HEIGHT + rowsForHeight(visibleWeeks);
  const handleComparisonEnabledChange = useCallback((enabled: boolean): void => {
    setComparisonEnabled(enabled);
    if (!enabled) {
      if (mode === 'single-comparison') {
        singleComparisonSelection.selectComparisonDate(null);
        (propsRef.current as SingleComparisonMode).onSelectComparisonDate(null);
      } else if (mode === 'range-comparison') {
        rangeComparisonSelection.comparisonRange.clearRange();
        (propsRef.current as RangeComparisonMode).onSelectComparisonRange(null);
      } else if (mode === 'range-overlap') {
        rangeOverlapSelection.comparisonRange.clearRange();
        (propsRef.current as RangeOverlapMode).onSelectComparisonRange(null);
      }
    }
  }, [mode, singleComparisonSelection, rangeComparisonSelection, rangeOverlapSelection]);
  const handleComparisonDateChange = useCallback((date_7: Date): void => {
    if (propsRef.current.mode === 'single-comparison') {
      // A typed date is a manual selection, same as a calendar click or drag.
      if (comparisonPresetRef.current !== CUSTOM_PRESET_VALUE) handleComparisonPresetChange(CUSTOM_PRESET_VALUE);
      singleComparisonSelection.selectComparisonDate(date_7);
      (propsRef.current as SingleComparisonMode).onSelectComparisonDate(date_7);
    }
  }, [singleComparisonSelection, handleComparisonPresetChange]);
  const handleSelectedRangeChange = useCallback((range_2: DateRange): void => {
    switch (propsRef.current.mode) {
      case 'range':
        {
          const vals_0 = [toCalendarDate(range_2.from)];
          if (range_2.to != null) vals_0.push(toCalendarDate(range_2.to));
          pendingRangePresetRef.current = null;
          zagApi.setValue(vals_0);
          notifySelectRange(range_2, CUSTOM_PRESET_VALUE);
          break;
        }
      case 'range-comparison':
        rangeComparisonSelection.mainRange.setRange(range_2);
        notifySelectRange(range_2, CUSTOM_PRESET_VALUE);
        syncComparisonToMainRange(range_2);
        break;
      case 'range-overlap':
        rangeOverlapSelection.mainRange.setRange(range_2);
        notifySelectRange(range_2, CUSTOM_PRESET_VALUE);
        syncComparisonToMainRange(range_2);
        break;
    }
  },
  // zagApi is a new object each render — use service (stable) as the dep

  [rangeComparisonSelection, rangeOverlapSelection, service, notifySelectRange, syncComparisonToMainRange]);
  const handleComparisonRangeChange = useCallback((range_3: DateRange): void => {
    const currentMode = propsRef.current.mode;
    if (currentMode !== 'range-comparison' && currentMode !== 'range-overlap') return;
    // A typed range is a manual selection, same as a calendar click or drag.
    if (comparisonPresetRef.current !== CUSTOM_PRESET_VALUE) handleComparisonPresetChange(CUSTOM_PRESET_VALUE);
    if (currentMode === 'range-comparison') {
      rangeComparisonSelection.comparisonRange.setRange(range_3);
      (propsRef.current as RangeComparisonMode).onSelectComparisonRange(range_3);
    } else {
      rangeOverlapSelection.comparisonRange.setRange(range_3);
      (propsRef.current as RangeOverlapMode).onSelectComparisonRange(range_3);
    }
  }, [rangeComparisonSelection, rangeOverlapSelection, handleComparisonPresetChange]);
  const handleDragRangeCommit = useCallback((range_4: DateRange): void => {
    const currentMode_0 = propsRef.current.mode;
    const isComparisonRangeMode = currentMode_0 === 'range-comparison' || currentMode_0 === 'range-overlap';
    if (!isComparisonRangeMode || !comparisonEnabled) {
      handleSelectedRangeChange(range_4);
      // A drag commit overrides any in-progress two-click selection: clear the
      // stale tempStart so the hover preview stops immediately.
      if (currentMode_0 === 'range-comparison') rangeComparisonSelection.mainRange.clearTempStart();else if (currentMode_0 === 'range-overlap') rangeOverlapSelection.mainRange.clearTempStart();
      return;
    }
    const selection = currentMode_0 === 'range-comparison' ? rangeComparisonSelection : rangeOverlapSelection;
    const hasMain = selection.mainRange.range != null;
    const hasComparison = selection.comparisonRange.range != null;

    // Second drag in the cycle: the user has already set the "Selected date"
    // range, so this drag fills in the "Compared to" range.
    if (hasMain && !hasComparison) {
      if (comparisonPresetRef.current !== CUSTOM_PRESET_VALUE) handleComparisonPresetChange(CUSTOM_PRESET_VALUE);
      handleComparisonRangeChange(range_4);
      // Clear any stale tempStart from a prior two-click first-click on the
      // comparison range so the hover preview stops immediately after drag.
      selection.comparisonRange.clearTempStart();
      return;
    }

    // First drag, or restart of the cycle. A preset-derived comparison was already
    // recomputed against the new range by `handleSelectedRangeChange`; a custom one
    // is cleared so the next drag completes a fresh pair.
    handleSelectedRangeChange(range_4);
    // Clear any stale tempStart from a prior two-click first-click on the
    // main range so the hover preview stops immediately after drag.
    selection.mainRange.clearTempStart();
    if (hasComparison && comparisonPresetRef.current === CUSTOM_PRESET_VALUE) {
      selection.comparisonRange.clearRange();
      if (currentMode_0 === 'range-comparison') {
        ;
        (propsRef.current as RangeComparisonMode).onSelectComparisonRange(null);
      } else {
        ;
        (propsRef.current as RangeOverlapMode).onSelectComparisonRange(null);
      }
    }
  }, [comparisonEnabled, rangeComparisonSelection, rangeOverlapSelection, handleSelectedRangeChange, handleComparisonRangeChange, handleComparisonPresetChange]);
  handleDragRangeCommitRef.current = handleDragRangeCommit;
  const handleApplyClick = useCallback(() => {
    onApplyDateRangeClick?.();
    onApply();
  }, [onApply, onApplyDateRangeClick]);
  const handleDateHover = useCallback((date_8: Date | null): void => {
    // `hoveredDate` is only consumed to paint the two-click preview (see
    // `getDateTileProps`, guarded by `pendingStart != null`). Outside that
    // window the value is unused, so skip the state update entirely — otherwise
    // every mouse move between tiles would re-render the whole picker. The idle
    // hover highlight is pure CSS (`:hover` in styles.ts).
    if (pendingStartRef.current != null) {
      const prev = hoveredDateRef.current;
      const changed = date_8 == null ? prev != null : prev?.getTime() !== date_8.getTime();
      if (changed) setHoveredDate(date_8);
    } else if (hoveredDateRef.current != null) {
      // Clear a stale value left over from a just-finished pending selection.
      setHoveredDate(null);
    }
    if (date_8 != null) dragSelection.onMouseEnter(date_8);
  }, [dragSelection.onMouseEnter]);
  return <StyledDatePickerContainer ref={ref} className={className} $isComparisonMode={isComparisonMode}>
      <StyledDatePickerBody>
        {showSidebar && <StyledSidebar>
            <StyledSidebarContent>
              {showPresets && <Presets presets={presets} onSelectPreset={handlePresetSelect} activePresetValue={activePresetValue} />}
              {showPresets && isComparisonMode && <Divider disablePadding />}
              {isComparisonMode && <ComparisonPanel enabled={comparisonEnabled} onEnabledChange={handleComparisonEnabledChange} lockEnabled={lockComparisonEnabled} selectedPreset={comparisonPreset} onSelectPreset={handleComparisonPresetChange} presets={comparisonPresetOptions} />}
            </StyledSidebarContent>
          </StyledSidebar>}

        <StyledDatePickerContent>
          {showSelectedHeader && <SelectedDateHeader selectedDate={selectedDate} selectedRange={selectedRange} selectedAsRange={isRangeMode} onDateChange={handleDateInputChange} onSelectedRangeChange={handleSelectedRangeChange} showComparison={isComparisonMode && comparisonEnabled} comparisonDate={comparisonDate} comparisonRange={comparisonRange} comparisonAsRange={mode === 'range-comparison' || mode === 'range-overlap'} onComparisonDateChange={handleComparisonDateChange} onComparisonRangeChange={handleComparisonRangeChange} disableTextEdit={disableTextEdit} color={color} firstDayOfWeek={firstDayOfWeek} />}

          <StyledCalendarContentWrapper ref={wrapperRef} $contentMaxHeight={calendarMaxHeight} onScroll={handleCalendarScroll} data-testid="datepicker-calendar-scroll">
            {monthsToRender.map((month, index) => <StyledMonthContainer key={month.toISOString()} ref={index === anchorIndex ? anchorMonthRef : undefined} role="group" aria-label={format(month, 'MMMM yyyy')}>
                <CalendarGrid currentMonth={month} minDate={minDate} maxDate={maxDate} disabled={disabled} showHeader firstDayOfWeek={firstDayOfWeek} shouldDisableDate={effectiveShouldDisableDate} onDateClick={handleDateClick} onDateHover={handleDateHover} onDateMouseDown={isRangeMode ? dragSelection.onMouseDown : undefined} getDateTileProps={getDateTileProps} getZagDateProps={useZag ? getZagDateProps : undefined} />
              </StyledMonthContainer>)}
          </StyledCalendarContentWrapper>

          {(() => {
          const defaultFooter = <StyledFooter>
                <Button variant="tonal" scheme="light" onClick={onCancel}>
                  {cancelText}
                </Button>
                <Button variant="filled" scheme="light" onClick={handleApplyClick} disabled={isApplyDisabled ?? !hasSelection}>
                  {applyText}
                </Button>
              </StyledFooter>;
          return renderCalendarInfo == null ? defaultFooter : renderCalendarInfo(defaultFooter);
        })()}
        </StyledDatePickerContent>
      </StyledDatePickerBody>

      {helperText != null && <StyledMessage>
          <Badge color={getInputBadgeColor(color)} />
          <span>{helperText}</span>
        </StyledMessage>}
    </StyledDatePickerContainer>;
};
DatePickerV2.displayName = 'DatePickerV2';
export default DatePickerV2;
export { DatePickerV2 };